import React, { useCallback, useEffect, useState } from "react";
import { SplitEmployeeDocumentFileDTO } from "../../../../models/split-document/split-employee-document/split-employee-document-file-dto";
import { Checkbox, Grid, IconButton, TableCell, TableRow, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faClose, faPen } from "@fortawesome/free-solid-svg-icons";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { EmployeesApiService } from "../../../../services/employees/employees-api.service";
import { EmployeesFilter } from "../../../../models/employees";
import { AddEditEmployees } from "../../../../views/legal-entities/EmployeesTable/AddEditEmployees/AddEditEmployees";
import { SplitEmployeeDocumentFilesApiService } from "../../../../services/split-document/split-employee-document/split-employee-document-files-api.service";
import { Popover } from "../../../../reusable-components/SimplePopover/styles";
import DocumentViewer from "../../../../reusable-components/DocumentViewer/DocumentViewer";
import { SplitEmployeeDocumentFileContentFilter } from "../../../../models/split-document/split-employee-document/split-employee-document-file-content-filter";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { customFileIconStyles } from "../../../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { CompanyLegalEntityRef } from "../../../../models/company-accounts/company-legal-entities";
interface Props {
  splitDocumentFile: SplitEmployeeDocumentFileDTO;
  handleSaveUpdate: (updatedFile: SplitEmployeeDocumentFileDTO) => void;
  handleSelectionChange: (updatedEeFile: SplitEmployeeDocumentFileDTO) => void;
  legalEntity: CompanyLegalEntityRef | null;
}

interface State {
  isBeingEdited: boolean;
  mutatedItem: SplitEmployeeDocumentFileDTO;
  loadingMatches: boolean;
  fileToView: {
    file: Blob;
    fileExtension: string;
    fileName: string;
  } | null;
}

const SplitEmployeeDocumentFileRow = ({
  splitDocumentFile,
  handleSaveUpdate,
  handleSelectionChange,
  legalEntity,
}: Props) => {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    isBeingEdited: false,
    mutatedItem: splitDocumentFile,
    loadingMatches: false,
    fileToView: null,
  });

  const handleUpdateMutatedItem = (
    field: keyof SplitEmployeeDocumentFileDTO,
    value: SplitEmployeeDocumentFileDTO[keyof SplitEmployeeDocumentFileDTO]
  ) => {
    setState((state) => ({
      ...state,
      mutatedItem: {
        ...state.mutatedItem,
        [field]: value,
      },
    }));
  };

  const handleCheckMatches = useCallback(
    async (autoSaveToParent: boolean) => {
      if (state.mutatedItem.employee) {
        setState((state) => ({ ...state, loadingMatches: true }));
        try {
          const response = await new SplitEmployeeDocumentFilesApiService().checkEmployeeMatching(
            splitDocumentFile.id,
            state.mutatedItem.employee.id
          );

          if (response.error) {
            setState((state) => ({ ...state, error: response.error }));
          } else {
            if (autoSaveToParent) {
              handleSaveUpdate({
                ...state.mutatedItem,
                nameMatching: response.items[0].nameMatching,
                addressMatching: response.items[0].addressMatching,
                taxIdMatching: response.items[0].taxIdMatching,
                birthDateMatching: response.items[0].birthDateMatching,
              });
            } else {
              setState((state) => ({
                ...state,
                mutatedItem: {
                  ...state.mutatedItem,
                  nameMatching: response.items[0].nameMatching,
                  addressMatching: response.items[0].addressMatching,
                  taxIdMatching: response.items[0].taxIdMatching,
                  birthDateMatching: response.items[0].birthDateMatching,
                },
              }));
            }
          }
        } catch (error) {
          setState((state) => ({ ...state, error: error }));
        } finally {
          setState((state) => ({ ...state, loadingMatches: false }));
        }
      }
    },
    [splitDocumentFile.id, state.mutatedItem, handleSaveUpdate]
  );

  const getEmployeeFilecontentCallback = useCallback(async () => {
    try {
      const response = await new SplitEmployeeDocumentFilesApiService().getEmployeeFileContent({
        splitEmployeeDocumentFileId: splitDocumentFile.id,
      } as SplitEmployeeDocumentFileContentFilter);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else if (response.file && response.file.size > 0) {
        setState((state) => ({
          ...state,
          error: "",
          fileToView: {
            file: response.file as Blob,
            fileExtension: "pdf",
            fileName: "Employee file",
          },
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error }));
    } finally {
    }
  }, []);

  const handleRemoveAllMatchings = useCallback(() => {
    setState((state) => ({
      ...state,
      mutatedItem: {
        ...state.mutatedItem,
        nameMatching: false,
        addressMatching: false,
        taxIdMatching: false,
        birthDateMatching: false,
      },
    }));
  }, []);

  useEffect(() => {
    if (!splitDocumentFile.employee) {
      //if file is not associated to employee
      //1. check matches
      //2. update parent with employee and matches
      if (state.mutatedItem.employee) {
        handleCheckMatches(true);
        // handleSaveUpdate({
        //   ...state.mutatedItem,
        // });
      } else {
        handleSaveUpdate({
          ...state.mutatedItem,
          nameMatching: false,
          addressMatching: false,
          taxIdMatching: false,
          birthDateMatching: false,
        });
      }
    } else {
      //if file is associated to employee
      //1. check matches
      //2. wait for confirmation to update parent with employee and matches
      if (state.mutatedItem.employee && state.mutatedItem.employee.id !== splitDocumentFile.employee.id) {
        handleCheckMatches(false);
      } else {
        handleRemoveAllMatchings();
      }
    }
  }, [state.mutatedItem.employee]);

  return (
    <>
      <TableRow>
        <TableCell
          rowSpan={state.isBeingEdited ? 2 : 1}
          align="center"
        >
          <Checkbox
            checked={splitDocumentFile.isApproved}
            onChange={() => {
              handleSelectionChange(splitDocumentFile);
            }}
            disabled={!splitDocumentFile.employee}
          />
        </TableCell>
        <TableCell rowSpan={state.isBeingEdited ? 2 : 1}>
          <Grid style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <IconButton onClick={() => getEmployeeFilecontentCallback()}>
              <FileIcon
                extension="pdf"
                {...customFileIconStyles["pdf" as DefaultExtensionType]}
                radius={2}
              />
            </IconButton>
            <Typography variant="body2">{`${splitDocumentFile.numberOfPages} page${
              splitDocumentFile.numberOfPages > 1 ? "s" : ""
            }`}</Typography>
          </Grid>
        </TableCell>
        <TableCell>
          <Grid style={{ display: "flex", alignItems: "center" }}>
            <Typography
              style={{ color: state.isBeingEdited ? theme.palette.secondary.light : theme.palette.text.primary }}
            >
              {splitDocumentFile.employee?.name}
            </Typography>
            {splitDocumentFile.employee ? (
              <IconButton
                style={{ color: state.isBeingEdited ? theme.palette.secondary.light : theme.palette.primary.main }}
                onClick={() =>
                  setState((state) => ({
                    ...state,
                    isBeingEdited: true,
                  }))
                }
                disabled={state.isBeingEdited}
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
            ) : (
              <Grid>
                <AutocompleteDropdown
                  serviceApi={new EmployeesApiService()}
                  field={"employee"}
                  handleChange={handleUpdateMutatedItem}
                  propsFilter={
                    {
                      legalEntityIds: [legalEntity?.id],
                    } as EmployeesFilter
                  }
                />
                <AddEditEmployees
                  effect="add"
                  buttonText="Employee not in the list, add a new one"
                  defaultLegalEntity={legalEntity}
                  buttonVariant="text"
                  customButtonStyle={{
                    color: theme.palette.primary.main,
                    fontSize: "12px",
                    textTransform: "none",
                  }}
                />
              </Grid>
            )}
          </Grid>
        </TableCell>
        {["nameMatching", "addressMatching", "birthDateMatching", "taxIdMatching"].map((field) => (
          <TableCell
            align="center"
            key={field}
          >
            {splitDocumentFile[field] ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                style={{ color: theme.palette.success.light }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleXmark}
                style={{ color: theme.palette.secondary.light }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>

      {/* THis will appear when the employee is being edited  */}
      {state.isBeingEdited ? (
        <TableRow>
          <TableCell>
            <Grid style={{ display: "flex" }}>
              <Grid>
                <AutocompleteDropdown
                  serviceApi={new EmployeesApiService()}
                  field={"employee"}
                  handleChange={handleUpdateMutatedItem}
                  propsFilter={
                    {
                      legalEntityIds: [legalEntity?.id],
                    } as EmployeesFilter
                  }
                />
                <AddEditEmployees
                  effect="add"
                  buttonText="Employee not in the list add a new one"
                  defaultLegalEntity={legalEntity}
                  buttonVariant="text"
                  customButtonStyle={{
                    color: theme.palette.primary.main,
                  }}
                />
              </Grid>
              <Grid style={{ display: "flex" }}>
                <IconButton
                  style={{ color: theme.palette.success.main }}
                  onClick={() => {
                    handleSaveUpdate(state.mutatedItem);
                    setState((state) => ({
                      ...state,
                      isBeingEdited: false,
                    }));
                  }}
                >
                  <FontAwesomeIcon icon={faCircleCheck} />
                </IconButton>

                <IconButton
                  style={{ color: theme.palette.error.main }}
                  onClick={() =>
                    setState((state) => ({
                      ...state,
                      mutatedItem: splitDocumentFile,
                      isBeingEdited: false,
                    }))
                  }
                >
                  <FontAwesomeIcon icon={faCircleXmark} />
                </IconButton>
              </Grid>
            </Grid>
          </TableCell>

          {["nameMatching", "addressMatching", "birthDateMatching", "taxIdMatching"].map((field) => (
            <TableCell
              align="center"
              key={field}
            >
              {state.mutatedItem[field] ? (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  style={{ color: theme.palette.success.light }}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  style={{ color: theme.palette.secondary.light }}
                />
              )}
            </TableCell>
          ))}
        </TableRow>
      ) : (
        <></>
      )}

      {state.fileToView && (
        <Popover
          open={!!state.fileToView}
          anchorPosition={{ left: 0, top: 0 }}
          anchorReference={"anchorPosition"}
        >
          <IconButton onClick={() => setState((state) => ({ ...state, fileToView: null }))}>
            <FontAwesomeIcon icon={faClose} />
          </IconButton>
          <DocumentViewer file={state.fileToView} />
        </Popover>
      )}
    </>
  );
};

export default SplitEmployeeDocumentFileRow;
